import { RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import type { SupplyPricePeriod } from '@obraplan/core/application/ports/requisition';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { periodoValido, textoDaAtualizacao } from './precos-da-obra';
import type { PrecosDaObraNaTela } from './use-precos-da-obra';

/**
 * O "Atualizar preços" da obra (Fase 29, D4 e D5).
 *
 * Tira uma foto nova do compras para **todos os materiais com código da obra** — e não só os
 * da tipologia aberta: o preço é do material na obra (D7), e atualizar metade deixaria o
 * orçamento com duas datas. A mão de obra fica de fora (o R$/h é da etapa 3).
 *
 * O período nasce o da obra — o da foto mais recente, ou os doze meses (D4) — e pode ser
 * trocado aqui; a foto nova o leva junto, e ele passa a ser o período da obra.
 *
 * A foto nova troca só a foto: o fator, o provisório e o digitado ficam (RN-398). Por isso
 * não há confirmação — atualizar não desfaz decisão de ninguém.
 */
export function AtualizarPrecos({
  precos,
  codigos,
}: {
  precos: PrecosDaObraNaTela;
  /** Os códigos da obra, contados só no clique: consolidar a obra a cada render é desperdício. */
  codigos: () => readonly string[];
}) {
  /** O período que alguém escolheu; ausente, vale o da obra, que pode chegar depois da leitura. */
  const [escolhido, setEscolhido] = useState<SupplyPricePeriod | undefined>(undefined);
  const [resultado, setResultado] = useState<string | undefined>(undefined);
  const periodo = escolhido ?? precos.periodo;
  const valido = periodoValido(periodo);

  const atualizar = async (): Promise<void> => {
    const lista = codigos();
    if (lista.length === 0) {
      setResultado('Nenhum material com código de compras na obra.');
      return;
    }
    setResultado(undefined);
    const feito = await precos.atualizar(lista, periodo);
    if (feito === undefined) return;
    if (feito.erro !== undefined) {
      // A recusa é dita como toda recusa do sistema; a linha conta o que já tinha ido.
      toast.error(feito.erro);
      const algum = feito.comMedia + feito.semMedia + feito.naoEncontrados > 0;
      setResultado(
        algum
          ? `${textoDaAtualizacao(feito)} — o resto não foi atualizado`
          : 'Os preços não foram atualizados.',
      );
      return;
    }
    setResultado(textoDaAtualizacao(feito));
  };

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
      <div className="flex flex-wrap items-center gap-1.5 text-xs text-(--color-content-muted)">
        <span>Período</span>
        <Input
          type="date"
          aria-label="Início do período dos preços"
          className="h-9 w-36"
          value={periodo.from}
          max={periodo.to}
          onChange={(event) => setEscolhido({ from: event.target.value, to: periodo.to })}
        />
        {/* O "a" anda com a data do fim: no celular, quebrar entre os dois deixa um "a" solto. */}
        <span className="flex items-center gap-1.5">
          <span>a</span>
          <Input
            type="date"
            aria-label="Fim do período dos preços"
            className="h-9 w-36"
            value={periodo.to}
            min={periodo.from}
            onChange={(event) => setEscolhido({ from: periodo.from, to: event.target.value })}
          />
        </span>
      </div>
      <Button
        variant="secondary"
        disabled={!valido || precos.atualizando}
        onClick={() => void atualizar()}
      >
        <RefreshCw className="size-4" aria-hidden="true" />
        {precos.atualizando ? 'Atualizando…' : 'Atualizar preços'}
      </Button>
      {/* Sempre presente, mesmo vazio: a região viva que nasce junto com o texto não é lida. */}
      <p role="status" className="text-xs text-(--color-content-muted) empty:hidden">
        {resultado}
      </p>
    </div>
  );
}
